/**
 * Shared filter option constants used across all pages.
 * Single source of truth — no more duplicated arrays per feature.
 */

// ── Period / Date Range Presets ─────────────────────────────────────

export type DatePreset = {
  key: string
  label: string
  days: number  // 0 = today
}

export const DATE_PRESETS: DatePreset[] = [
  { key: 'today', label: 'Today', days: 0 },
  { key: '7d', label: 'Last 7 days', days: 7 },
  { key: '14d', label: 'Last 14 days', days: 14 },
  { key: '30d', label: 'Last 30 days', days: 30 },
  { key: '90d', label: 'Last 90 days', days: 90 },
  { key: '12m', label: 'Last 12 months', days: 365 },
]

/** Convert a preset key to { date_from, date_to } ISO strings */
export function presetToRange(key: string): { date_from: string; date_to: string } {
  const now = new Date()
  const preset = DATE_PRESETS.find((p) => p.key === key)
  const days = preset?.days ?? 30
  const from = new Date(now)
  from.setDate(from.getDate() - days)
  return {
    date_from: from.toISOString().split('T')[0],
    date_to: now.toISOString().split('T')[0],
  }
}

// ── Specialty ───────────────────────────────────────────────────────

export const SPECIALTY_OPTIONS = [
  { value: '__all__', label: 'All Specialties' },
  { value: 'pediatrics', label: 'Pediatrics' },
  { value: 'family_medicine', label: 'Family Medicine' },
  { value: 'gastroenterology', label: 'Gastroenterology' },
  { value: 'ophthalmology', label: 'Ophthalmology' },
  { value: 'obgyn', label: 'OB/GYN' },
  { value: 'radiology', label: 'Radiology' },
  { value: 'orthopedics', label: 'Orthopedics' },
] as const

// ── Payer ───────────────────────────────────────────────────────────

export const PAYER_OPTIONS = [
  { value: '__all__', label: 'All Payers' },
  { value: 'medicare', label: 'Medicare' },
  { value: 'medicaid', label: 'Medicaid' },
  { value: 'commercial', label: 'Commercial' },
  { value: 'self_pay', label: 'Self Pay' },
] as const

// ── Encounter / Pipeline Status ─────────────────────────────────────

export const ENCOUNTER_STATUS_OPTIONS = [
  { value: '__all__', label: 'All Statuses' },
  { value: 'received', label: 'Received' },
  { value: 'copilot', label: 'Copilot' },
  { value: 'autopilot', label: 'Autopilot' },
  { value: 'coding_complete', label: 'Coding Complete' },
  { value: 'exported', label: 'Exported' },
] as const

// ── Priority ────────────────────────────────────────────────────────

export const PRIORITY_OPTIONS = [
  { value: '__all__', label: 'All Priorities' },
  { value: 'critical', label: 'Critical' },
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
] as const

// ── SLA Risk ────────────────────────────────────────────────────────

export const SLA_RISK_OPTIONS = [
  { value: '__all__', label: 'All SLA' },
  { value: 'on_time', label: 'On Track' },
  { value: 'at_risk', label: 'At Risk' },
  { value: 'overdue', label: 'Overdue' },
] as const

// ── Assignment Strategy ─────────────────────────────────────────────

export const ASSIGNMENT_STRATEGY_OPTIONS = [
  { value: 'round_robin', label: 'Round Robin' },
  { value: 'skill_weighted', label: 'Skill-Weighted' },
  { value: 'least_loaded', label: 'Least Loaded' },
] as const

// ── Report Format ───────────────────────────────────────────────────

export const REPORT_FORMAT_OPTIONS = [
  { value: 'pdf', label: 'PDF' },
  { value: 'csv', label: 'CSV' },
  { value: 'xlsx', label: 'Excel (XLSX)' },
] as const

// ── Period for simple selectors (fallback) ──────────────────────────

export const PERIOD_OPTIONS = [
  { value: 'today', label: 'Today' },
  { value: '7d', label: 'Last 7 days' },
  { value: '14d', label: 'Last 14 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: '90d', label: 'Last 90 days' },
] as const

export const PERIOD_OPTIONS_WITH_12M = [
  ...PERIOD_OPTIONS,
  { value: '12m', label: 'Last 12 months' },
] as const
